/**
 * StatusBadge Component
 *
 * Displays the current phase of AI blog post generation with appropriate colors and icons.
 * Supports all 9 phases including two approval phases (headline and outline).
 *
 * Story: BLOG-AI-003, AC-6, Task 4
 */
import { Loader2, Check, AlertCircle, Sparkles, FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type AIPhase =
  | 'researching'
  | 'generating_headlines'
  | 'awaiting_headline_approval'
  | 'outlining'
  | 'awaiting_outline_approval'
  | 'writing'
  | 'awaiting_length_decision'
  | 'optimizing'
  | 'ready'
  | 'failed'

interface StatusBadgeProps {
  phase: AIPhase | null
  error?: string
  className?: string
}

export function StatusBadge({ phase, error, className }: StatusBadgeProps) {
  // Don't render if no phase (manual post)
  if (!phase) return null

  const getPhaseConfig = (phase: AIPhase) => {
    switch (phase) {
      case 'researching':
        return {
          label: 'Researching...',
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          className: 'bg-blue-600 dark:bg-blue-700 text-white',
        }
      case 'generating_headlines':
        return {
          label: 'Generating Headlines...',
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          className: 'bg-indigo-600 dark:bg-indigo-700 text-white',
        }
      case 'awaiting_headline_approval':
        return {
          label: 'Choose Your Headline',
          icon: <Sparkles className="h-3 w-3 animate-pulse" />,
          className: 'bg-amber-600 dark:bg-amber-700 text-white',
        }
      case 'outlining':
        return {
          label: 'Creating Outline...',
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          className: 'bg-purple-600 dark:bg-purple-700 text-white',
        }
      case 'awaiting_outline_approval':
        return {
          label: 'Review Outline',
          icon: <FileText className="h-3 w-3 animate-pulse" />,
          className: 'bg-amber-600 dark:bg-amber-700 text-white',
        }
      case 'writing':
        return {
          label: 'Writing...',
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          className: 'bg-orange-600 dark:bg-orange-700 text-white',
        }
      case 'awaiting_length_decision':
        return {
          label: 'Review Content Length',
          icon: <FileText className="h-3 w-3 animate-pulse" />,
          className: 'bg-amber-600 dark:bg-amber-700 text-white',
        }
      case 'optimizing':
        return {
          label: 'Optimizing...',
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          className: 'bg-green-600 dark:bg-green-700 text-white',
        }
      case 'ready':
        return {
          label: 'Ready!',
          icon: <Check className="h-3 w-3" />,
          className: 'bg-green-600 dark:bg-green-700 text-white',
        }
      case 'failed':
        return {
          label: 'Failed',
          icon: <AlertCircle className="h-3 w-3" />,
          className: 'bg-destructive text-destructive-foreground',
        }
      default:
        return {
          label: 'Unknown',
          icon: null,
          className: 'bg-muted text-muted-foreground',
        }
    }
  }

  const config = getPhaseConfig(phase)

  return (
    <Badge
      variant="secondary"
      className={cn(
        'inline-flex items-center gap-1.5 font-medium',
        config.className,
        className
      )}
      title={error || undefined}
    >
      {config.icon}
      <span>{config.label}</span>
      {phase === 'failed' && error && (
        <span className="text-xs opacity-90" title={error}>
          (Hover for details)
        </span>
      )}
    </Badge>
  )
}
